import { Vector3, MeshBasicMaterial, DoubleSide } from 'three';
import { Halfedge, Vertex } from '@thirdPartyLibraries/three-mesh-halfedge/src';
import { adjacenceBoundaries } from '@thirdPartyLibraries/three-mesh-halfedge/src/core/Vertex';
import SortedArray from '@common/SortedArray';
import HalfEdgeMesh from '@three/common/halfEdgeMesh/HalfEdgeMesh';


export class AdvancingFrontFiller {
  private mesh: HalfEdgeMesh;
  private mergeThreshold: number;
  private maxIterations: number;
  private lowest: boolean;

  constructor(mesh: HalfEdgeMesh, mergeThreshold = 0.15, maxIterations = 12000, lowest = false) {
    this.mesh = mesh;
    this.mergeThreshold = mergeThreshold;
    this.maxIterations = maxIterations;
    this.lowest = lowest;
  }

  /**
   * Validates that the provided boundary loop is actually connected.
   * A valid connected loop means each vertex has at least one boundary halfedge
   * connecting it to another vertex in the loop.
   * 
   * @param boundaryLoop Array of vertices to validate
   * @returns Object with isValid boolean, optional error message, and isOpenArc flag
   */
  public static validateBoundaryLoop(boundaryLoop: Vertex[]): { isValid: boolean; error?: string; isOpenArc?: boolean } {
    if (boundaryLoop.length < 3) {
      return { isValid: false, error: 'Boundary loop must have at least 3 vertices' };
    }

    const loopVertexSet = new Set<Vertex>(boundaryLoop);
    
    // Check that each vertex is actually a boundary vertex
    for (const vertex of boundaryLoop) {
      if (!vertex.isFree()) {
        return { isValid: false, error: `Vertex ${vertex.id} is not a boundary vertex (not free)` };
      }
      if (vertex.halfedge === null) {
        return { isValid: false, error: `Vertex ${vertex.id} has no halfedge (isolated vertex)` };
      }
    }

    // Check connectivity: each vertex should have at least one boundary halfedge 
    // connecting to another vertex in the loop
    let connectedVertices = 0;
    for (const vertex of boundaryLoop) {
      let hasConnectionInLoop = false;
      
      // Check outgoing boundary halfedges
      for (const he of vertex.boundaryHalfedgesOutLoop()) {
        if (he.face === null && loopVertexSet.has(he.twin.vertex)) {
          hasConnectionInLoop = true;
          break;
        }
      }
      
      // Also check incoming if outgoing didn't find connection
      if (!hasConnectionInLoop) {
        for (const he of vertex.boundaryHalfedgesInLoop()) {
          if (he.face === null && loopVertexSet.has(he.vertex)) {
            hasConnectionInLoop = true;
            break;
          }
        }
      }
      
      if (hasConnectionInLoop) {
        connectedVertices++;
      }
    }

    // All vertices should be connected to at least one other vertex in the loop
    if (connectedVertices < boundaryLoop.length) {
      return { 
        isValid: false, 
        error: `Only ${connectedVertices}/${boundaryLoop.length} vertices are connected within the loop. The loop may not be contiguous.` 
      };
    }

    // Additional check: verify the loop forms a single connected component
    // Start from the first vertex and try to walk the boundary
    const visited = new Set<Vertex>();
    const queue: Vertex[] = [boundaryLoop[0]];
    visited.add(boundaryLoop[0]);

    while (queue.length > 0) {
      const current = queue.shift()!;
      
      // Find neighbors within the loop via boundary halfedges
      for (const he of current.boundaryHalfedgesOutLoop()) {
        if (he.face === null) {
          const neighbor = he.twin.vertex;
          if (loopVertexSet.has(neighbor) && !visited.has(neighbor)) {
            visited.add(neighbor);
            queue.push(neighbor);
          }
        }
      }
      for (const he of current.boundaryHalfedgesInLoop()) {
        if (he.face === null) {
          const neighbor = he.vertex;
          if (loopVertexSet.has(neighbor) && !visited.has(neighbor)) {
            visited.add(neighbor);
            queue.push(neighbor);
          }
        }
      }
    }

    if (visited.size !== boundaryLoop.length) {
      return { 
        isValid: false, 
        error: `Loop is not fully connected. Only ${visited.size}/${boundaryLoop.length} vertices are reachable from the starting vertex.` 
      };
    }

    // Check if it's a closed loop or open arc
    // Count internal halfedges (both endpoints in loop)
    const internalHalfedges = AdvancingFrontFiller.collectLoopHalfedges(boundaryLoop);
    const isClosedLoop = internalHalfedges.size === boundaryLoop.length;
    
    if (!isClosedLoop) {
      console.warn(`validateBoundaryLoop: Input is an open arc, not a closed loop (${internalHalfedges.size} halfedges for ${boundaryLoop.length} vertices). Will attempt to fill anyway.`);
    }

    return { isValid: true, isOpenArc: !isClosedLoop };
  }

  /**
   * Collects all boundary halfedges that belong to a specific loop.
   * A halfedge belongs to the loop if both its start and end vertices are in the loop.
   * 
   * @param boundaryLoop Array of vertices forming the boundary loop
   * @returns Set of halfedges that belong to this specific loop
   */
  public static collectLoopHalfedges(boundaryLoop: Vertex[]): Set<Halfedge> {
    const loopVertexSet = new Set<Vertex>(boundaryLoop);
    const loopHalfedges = new Set<Halfedge>();

    for (const vertex of boundaryLoop) {
      // Collect outgoing boundary halfedges where the destination is also in the loop
      for (const he of vertex.boundaryHalfedgesOutLoop()) {
        if (he.face === null) {
          const nextVertex = he.twin.vertex;
          if (loopVertexSet.has(nextVertex)) {
            loopHalfedges.add(he);
          }
        }
      }
      // Collect incoming boundary halfedges where the source is also in the loop
      for (const he of vertex.boundaryHalfedgesInLoop()) {
        if (he.face === null) {
          const prevVertex = he.vertex;
          if (loopVertexSet.has(prevVertex)) {
            loopHalfedges.add(he);
          }
        }
      }
    }

    return loopHalfedges;
  }

  /**
   * Classical advancing‑front hole‑filling algorithm.
   * Creates 1 / 2 / 3 triangles per step based on the interior
   * angle (< 75°, 75–135°, > 135°).
   * 
   * @param targetLoop Optional array of vertices forming a specific boundary loop to fill.
   *        If provided, only this loop will be filled, leaving other holes untouched.
   * @returns Set of vertex IDs that were patched
   */
  public async fillHoles(targetLoop?: Vertex[]): Promise<Set<number>> {
    this.mesh.material = new MeshBasicMaterial({ color: 0x00ff00, side: DoubleSide, vertexColors: true });

    console.log(`fillHoles: starting${targetLoop ? ` with targetLoop of ${targetLoop.length} vertices` : ' (all holes)'}`);

    let currentIteration = 0;
    let stalledIterations = 0;
    let vertexToBoundaryInfo = new Map<Vertex, adjacenceBoundaries>();
    
    // Setup for specific loop filling
    let excludeVertices: Set<Vertex> | undefined;
    let allowedHalfedges: Set<Halfedge> | undefined;
    let loopVertexSet: Set<Vertex> | undefined;
    
    if (targetLoop && targetLoop.length >= 3) {
      // Validate that the boundary loop is actually connected
      console.log('fillHoles: validating boundary loop...');
      const validation = AdvancingFrontFiller.validateBoundaryLoop(targetLoop);
      if (!validation.isValid) {
        console.warn(`Invalid boundary loop: ${validation.error}. Returning empty result.`);
        return new Set<number>();
      }
      console.log('fillHoles: boundary loop is valid');
      
      loopVertexSet = new Set<Vertex>(targetLoop);
      allowedHalfedges = AdvancingFrontFiller.collectLoopHalfedges(targetLoop);
      console.log(`fillHoles: collected ${allowedHalfedges.size} halfedges for the loop`);
      
      // Additional safety check: if no halfedges were collected, the loop isn't properly connected
      if (allowedHalfedges.size === 0) {
        console.warn('No boundary halfedges found in the loop. The vertices may not form a connected boundary.');
        return new Set<number>();
      }
      
      // Exclude vertices that are on boundary but NOT in our target loop
      const allBorderVertices = this.mesh.getBorderVertices();
      excludeVertices = new Set<Vertex>(
        allBorderVertices.filter((v) => !loopVertexSet!.has(v))
      );
      console.log(`fillHoles: excluding ${excludeVertices.size} vertices not in target loop`);
    }
    
    const initialBoundaryVertices = new Set<number>(
      this.mesh.getBorderVertices().map((v) => v.id)
    );

    const originalVerticesIds = new Set<number>(this.mesh.halfEdges.vertices.map((v) => v.id));

    // Track vertices that are stuck without boundary info
    let stuckVerticesIterations = 0;
    let lastStuckVertexIds: Set<number> = new Set();

    // Helper to check if we should continue the loop
    const shouldContinue = (): boolean => {
      if (targetLoop && loopVertexSet) {
        // For specific loop: continue while there are loop vertices still on boundary
        const remainingLoopVertices = Array.from(loopVertexSet).filter((v) => v.isFree() && v.halfedge !== null);
        
        // Need at least 3 vertices to form a triangle
        if (remainingLoopVertices.length <= 2) {
          console.log(`fillHoles: shouldContinue=false, only ${remainingLoopVertices.length} remaining vertices`);
          return false;
        }
        
        // Also check that we have valid halfedges connecting these vertices
        if (allowedHalfedges && allowedHalfedges.size === 0) {
          console.log('fillHoles: shouldContinue=false, no allowed halfedges');
          return false;
        }
        
        return true;
      }
      return this.mesh.getBorderVertices().length !== 0;
    };

    console.log('fillHoles: entering main loop');
    
    while (shouldContinue()) {
      let { sortedArray: front } = this.mesh.sortBorderVerticesByAngle(
        vertexToBoundaryInfo, 
        10000, 
        this.lowest,
        excludeVertices,
        allowedHalfedges
      );
      vertexToBoundaryInfo = new Map<Vertex, adjacenceBoundaries>();
      const newIterationVertices: Vertex[] = [];

      currentIteration++;
      
      // Calculate remaining vertices for logging
      const remainingVerts = targetLoop && loopVertexSet 
        ? Array.from(loopVertexSet).filter((v) => v.isFree() && v.halfedge !== null).length
        : this.mesh.getBorderVertices().length;
      
      // Log every 100 iterations, or when we're close to finishing (< 10 remaining)
      if (currentIteration % 100 === 0 || remainingVerts <= 10) {
        console.log(`fillHoles: iteration ${currentIteration}, front size: ${front.length()}, remaining vertices: ${remainingVerts}, stalled: ${stalledIterations}, allowedHalfedges: ${allowedHalfedges?.size ?? 'N/A'}`);
        
        // If front is much smaller than remaining vertices, log details
        if (front.length() < remainingVerts - 1 && remainingVerts <= 10) {
          console.log('fillHoles: DEBUG - some vertices not getting into front. Remaining vertices:');
          if (loopVertexSet) {
            const freeVerts = Array.from(loopVertexSet).filter((v) => v.isFree() && v.halfedge !== null);
            for (const v of freeVerts) {
              const boundaryInfo = v.getBoundaryHalfedgesAndVerticesInfo(allowedHalfedges);
              console.log(`  - vertex ${v.id}: boundaryInfo=${boundaryInfo ? 'YES' : 'NO'}, isFree=${v.isFree()}`);
            }
          }
        }
      }
      
      if (currentIteration >= this.maxIterations) {
        console.warn(`fillHoles: maxIterations (${this.maxIterations}) reached, stopping.`);
        break;
      }

      // If front is empty, no valid boundary vertices were found
      if (front.length() === 0) {
        console.warn(`fillHoles: No boundary vertices in front at iteration ${currentIteration}, stopping.`);
        break;
      }

      let boundaryInfo = front.popSmallest();
      if (!boundaryInfo) {
        console.warn(`fillHoles: No boundary info available at iteration ${currentIteration}, stopping.`);
        break;
      }

      const preFaceCount = this.mesh.halfEdges.faces.length;

      // Process based on angle
      if (boundaryInfo.angle < 75) {
        await this.handleSmallAngle(boundaryInfo, vertexToBoundaryInfo, front);
      } else if (boundaryInfo.angle >= 75 && boundaryInfo.angle < 125) {
        const newVertex = await this.handleMediumAngle(boundaryInfo, vertexToBoundaryInfo, front);
        if (newVertex) {
          newIterationVertices.push(newVertex);
          // Add new vertex to loop tracking if filling specific loop
          if (loopVertexSet) {
            loopVertexSet.add(newVertex);
          }
        }
      } else if (boundaryInfo.angle >= 125) {
        const newVertices = await this.handleLargeAngle(boundaryInfo, vertexToBoundaryInfo, front);
        newIterationVertices.push(...newVertices);
        // Add new vertices to loop tracking if filling specific loop
        if (loopVertexSet) {
          newVertices.forEach((v) => loopVertexSet!.add(v));
        }
      }

      // Apply vertex merging if new vertices were created
      if (newIterationVertices.length > 0) {
        await this.processVertexMerging(newIterationVertices, vertexToBoundaryInfo, front, initialBoundaryVertices);
      }
      
      this.mesh.calculateBorderVertices();
      
      // Update exclude set and allowed halfedges for specific loop filling
      if (targetLoop && loopVertexSet) {
        // Update loopVertexSet - remove vertices no longer on boundary
        for (const vertex of Array.from(loopVertexSet)) {
          if (!vertex.isFree() || vertex.halfedge === null) {
            loopVertexSet.delete(vertex);
          }
        }
        
        // Rebuild allowed halfedges with current loop vertices
        allowedHalfedges = AdvancingFrontFiller.collectLoopHalfedges(Array.from(loopVertexSet));
        
        // If no more halfedges in the loop, we're done
        if (allowedHalfedges.size === 0) {
          console.warn('fillHoles: No more halfedges in loop, stopping.');
          break;
        }
        
        // Update exclude set
        const allBorderVertices = this.mesh.getBorderVertices();
        excludeVertices = new Set<Vertex>(
          allBorderVertices.filter((v) => !loopVertexSet!.has(v))
        );
      }

      // Stall detection
      const postFaceCount = this.mesh.halfEdges.faces.length;
      const facesCreated = Math.max(0, postFaceCount - preFaceCount);
      
      if (facesCreated === 0) {
        stalledIterations++;
        // More aggressive stall threshold - stop after 20 stalled iterations or 
        // after stalling more times than remaining vertices
        const remainingVerticesForStall = targetLoop 
          ? Array.from(loopVertexSet!).filter((v) => v.isFree()).length 
          : this.mesh.getBorderVertices().length;
        const stallThreshold = Math.min(20, remainingVerticesForStall + 5);
        
        if (stalledIterations > stallThreshold) {
          console.warn(`fillHoles: Stalled for ${stalledIterations} iterations (threshold: ${stallThreshold}), stopping.`);
          break;
        }
      } else {
        stalledIterations = 0;
      }
      
      // Safety: Check for vertices stuck without boundary info (loop fragmentation detection)
      if (targetLoop && loopVertexSet) {
        const currentFreeVerts = Array.from(loopVertexSet).filter((v) => v.isFree() && v.halfedge !== null);
        const currentVertsWithoutBoundaryInfo = currentFreeVerts.filter(
          (v) => !v.getBoundaryHalfedgesAndVerticesInfo(allowedHalfedges)
        );
        
        if (currentVertsWithoutBoundaryInfo.length >= 2) {
          const currentStuckIds = new Set(currentVertsWithoutBoundaryInfo.map((v) => v.id));
          
          // Check if the same vertices are stuck
          const sameVerticesStuck = currentStuckIds.size === lastStuckVertexIds.size &&
            [...currentStuckIds].every((id) => lastStuckVertexIds.has(id));
          
          if (sameVerticesStuck) {
            stuckVerticesIterations++;
          } else {
            stuckVerticesIterations = 1;
            lastStuckVertexIds = currentStuckIds;
          }
          
          // Debug: On first detection, log detailed info about stuck vertices
          if (stuckVerticesIterations === 1) {
            console.log('fillHoles: DEBUG - Analyzing stuck vertices:');
            for (const v of currentVertsWithoutBoundaryInfo) {
              // Get all boundary halfedges for this vertex (outgoing)
              const outgoingBoundaryHalfedges: Halfedge[] = [...v.boundaryHalfedgesOutLoop()];
              // Get all boundary halfedges arriving at this vertex (incoming)
              const incomingBoundaryHalfedges: Halfedge[] = [...v.boundaryHalfedgesInLoop()];
              
              const outAllowedCount = outgoingBoundaryHalfedges.filter((he) => allowedHalfedges?.has(he)).length;
              const inAllowedCount = incomingBoundaryHalfedges.filter((he) => allowedHalfedges?.has(he)).length;
              const boundaryInfoWithoutFilter = v.getBoundaryHalfedgesAndVerticesInfo();
              
              console.log(`  Vertex ${v.id}:`);
              console.log(`    - Outgoing boundary halfedges: ${outgoingBoundaryHalfedges.length}, in allowed: ${outAllowedCount}`);
              console.log(`    - Incoming boundary halfedges: ${incomingBoundaryHalfedges.length}, in allowed: ${inAllowedCount}`);
              console.log(`    - Has boundary info WITHOUT filter: ${boundaryInfoWithoutFilter !== null}`);
              
              // Log details of outgoing boundary halfedges
              for (const he of outgoingBoundaryHalfedges) {
                const startInLoop = loopVertexSet?.has(he.vertex);
                const endInLoop = he.next ? loopVertexSet?.has(he.next.vertex) : false;
                const inAllowed = allowedHalfedges?.has(he);
                console.log(`    - OUT HE from ${he.vertex.id} to ${he.next?.vertex.id}: startInLoop=${startInLoop}, endInLoop=${endInLoop}, inAllowed=${inAllowed}`);
              }
              
              // Log details of incoming boundary halfedges  
              for (const he of incomingBoundaryHalfedges) {
                const startInLoop = loopVertexSet?.has(he.vertex);
                const endInLoop = he.next ? loopVertexSet?.has(he.next.vertex) : false;
                const inAllowed = allowedHalfedges?.has(he);
                console.log(`    - IN HE from ${he.vertex.id} to ${he.next?.vertex.id}: startInLoop=${startInLoop}, endInLoop=${endInLoop}, inAllowed=${inAllowed}`);
              }
            }
          }
          
          // If same vertices are stuck for 3+ iterations, try to handle open arc endgame
          if (stuckVerticesIterations >= 3) {
            // Check if we have exactly 2 stuck vertices (arc endpoints) and they form an open arc
            if (currentVertsWithoutBoundaryInfo.length === 2 && currentFreeVerts.length <= 5) {
              console.log('fillHoles: Detected open arc endgame - attempting to close arc directly');
              
              const endpoint1 = currentVertsWithoutBoundaryInfo[0];
              const endpoint2 = currentVertsWithoutBoundaryInfo[1];
              
              // Find the middle vertices (those with boundary info)
              const middleVertices = currentFreeVerts.filter(
                (v) => v !== endpoint1 && v !== endpoint2 && v.getBoundaryHalfedgesAndVerticesInfo(allowedHalfedges)
              );
              
              console.log(`fillHoles: Arc has ${middleVertices.length} middle vertices between endpoints ${endpoint1.id} and ${endpoint2.id}`);
              
              // Try to close the arc by creating triangles connecting endpoints through middle vertices
              let arcClosed = false;
              
              if (middleVertices.length === 1) {
                // Simple case: 3 vertices total, create one triangle
                const middleVertex = middleVertices[0];
                try {
                  this.mesh.halfEdges.createFace(endpoint1, middleVertex, endpoint2);
                  console.log(`fillHoles: Created closing triangle: ${endpoint1.id} - ${middleVertex.id} - ${endpoint2.id}`);
                  arcClosed = true;
                } catch (e) {
                  // Try reverse orientation
                  try {
                    this.mesh.halfEdges.createFace(endpoint1, endpoint2, middleVertex);
                    console.log(`fillHoles: Created closing triangle (reversed): ${endpoint1.id} - ${endpoint2.id} - ${middleVertex.id}`);
                    arcClosed = true;
                  } catch (e2) {
                    console.warn(`fillHoles: Failed to create closing triangle: ${e2}`);
                  }
                }
              } else if (middleVertices.length === 0 && currentFreeVerts.length === 3) {
                // Edge case: the third vertex might be newly created and have boundary info
                const thirdVertex = currentFreeVerts.find((v) => v !== endpoint1 && v !== endpoint2);
                if (thirdVertex) {
                  try {
                    this.mesh.halfEdges.createFace(endpoint1, thirdVertex, endpoint2);
                    console.log(`fillHoles: Created closing triangle with third vertex: ${endpoint1.id} - ${thirdVertex.id} - ${endpoint2.id}`);
                    arcClosed = true;
                  } catch (e) {
                    try {
                      this.mesh.halfEdges.createFace(endpoint1, endpoint2, thirdVertex);
                      console.log(`fillHoles: Created closing triangle (reversed): ${endpoint1.id} - ${endpoint2.id} - ${thirdVertex.id}`);
                      arcClosed = true;
                    } catch (e2) {
                      console.warn(`fillHoles: Failed to create closing triangle: ${e2}`);
                    }
                  }
                }
              }
              
              if (arcClosed) {
                // Update state and continue
                this.mesh.calculateBorderVertices();
                stuckVerticesIterations = 0;
                lastStuckVertexIds.clear();
                continue;
              }
            }
            
            // If we couldn't close the arc after 10 iterations, give up
            if (stuckVerticesIterations >= 10) {
              console.warn(`fillHoles: ${currentVertsWithoutBoundaryInfo.length} vertices (${[...currentStuckIds].join(', ')}) stuck without boundary info for ${stuckVerticesIterations} iterations. Loop is fragmented. Stopping.`);
              break;
            }
          }
        } else {
          stuckVerticesIterations = 0;
          lastStuckVertexIds.clear();
        }
      }
    }

    console.log(`fillHoles: finished after ${currentIteration} iterations`);
    
    const patchedIds: Set<number> = new Set();
    this.mesh.halfEdges.vertices.forEach((v) => {
      if (!originalVerticesIds.has(v.id)) {
        patchedIds.add(v.id);
      }
    });
    console.log(`fillHoles: created ${patchedIds.size} new vertices`);
    this.mesh.calculateBorderVertices();
    return patchedIds;
  }

  /**
   * Advancing‑front variant that stops after adding up to `maxFaces` faces.
   * Each step chooses the next candidate among the `topN` highest-Y border vertices
   * (i.e., we restrict the active frontier to the highest vertices before sorting by angle).
   *
   * @param maxFaces Maximum number of faces to add (upper bound)
   * @param topN Number of highest-Y border vertices to consider at each step
   * @returns Object with patched vertex IDs and the number of faces actually added
   */
  public async fillHolesLimited(maxFaces: number, topN: number): Promise<{ patchedIds: Set<number>; facesAdded: number }> {
    this.mesh.material = new MeshBasicMaterial({ color: 0x00ff00, side: DoubleSide, vertexColors: true });

    let facesAdded = 0;
    let currentIteration = 0;
    let vertexToBoundaryInfo = new Map<Vertex, adjacenceBoundaries>();
    const initialBoundaryVertices = new Set<number>(this.mesh.getBorderVertices().map((v) => v.id));
    const originalVerticesIds = new Set<number>(this.mesh.halfEdges.vertices.map((v) => v.id));

    while (this.mesh.getBorderVertices().length !== 0 && facesAdded < maxFaces) {
      // Pick candidates among the topN HIGHEST-Y vertices, then sort those by angle
      let { sortedArray: front } = this.mesh.sortBorderVerticesByAngle(vertexToBoundaryInfo, topN, /*lowest*/ false);
      vertexToBoundaryInfo = new Map<Vertex, adjacenceBoundaries>();
      const newIterationVertices: Vertex[] = [];

      currentIteration++;
      if (currentIteration >= this.maxIterations) break;

      const preFaceCount = this.mesh.halfEdges.faces.length;

      const boundaryInfo = front.popSmallest();
      if (!boundaryInfo) break;

      // Process based on angle
      if (boundaryInfo.angle < 75) {
        await this.handleSmallAngle(boundaryInfo, vertexToBoundaryInfo, front);
      } else if (boundaryInfo.angle >= 75 && boundaryInfo.angle < 125) {
        const newVertex = await this.handleMediumAngle(boundaryInfo, vertexToBoundaryInfo, front);
        if (newVertex) newIterationVertices.push(newVertex);
      } else if (boundaryInfo.angle >= 125) {
        const newVertices = await this.handleLargeAngle(boundaryInfo, vertexToBoundaryInfo, front);
        newIterationVertices.push(...newVertices);
      }

      // Apply vertex merging if new vertices were created
      if (newIterationVertices.length > 0) {
        await this.processVertexMerging(newIterationVertices, vertexToBoundaryInfo, front, initialBoundaryVertices);
      }

      this.mesh.calculateBorderVertices();

      const postFaceCount = this.mesh.halfEdges.faces.length;
      facesAdded += Math.max(0, postFaceCount - preFaceCount);
      if (facesAdded >= maxFaces) break;
    }

    const patchedIds: Set<number> = new Set();
    this.mesh.halfEdges.vertices.forEach((v) => {
      if (!originalVerticesIds.has(v.id)) patchedIds.add(v.id);
    });
    this.mesh.calculateBorderVertices();
    return { patchedIds, facesAdded };
  }

  /**
   * Advancing-front fill that sweeps across the boundary according to a height metric.
   * By default the algorithm starts from the highest vertices (classic top-down sweep).
   * Setting `lowestFirst` to true inverts the sweep so that the frontier grows from
   * the lowest vertices upward instead (bottom-up).
   */
  public async fillHolesTopDown(
    maxFaces: number,
    sweepDirection?: Vector3,
    lowestFirst = false,
  ): Promise<{ patchedIds: Set<number>; facesAdded: number }> {
    this.mesh.material = new MeshBasicMaterial({ color: 0x00ff00, side: DoubleSide, vertexColors: true });

    let facesAdded = 0;
    let currentIteration = 0;
    let stalledIterations = 0;
    const evalDir = sweepDirection && sweepDirection.lengthSq() > 1e-12 ? sweepDirection.clone().normalize() : undefined;
    const baseHeightOf = (vertex: Vertex) => (evalDir ? vertex.position.dot(evalDir) : vertex.position.y);
    const heightOf = lowestFirst ? (vertex: Vertex) => -baseHeightOf(vertex) : baseHeightOf;
    const vertexToBoundaryInfo = new Map<Vertex, adjacenceBoundaries>();
    const front = new SortedArray<adjacenceBoundaries>((item) => item.angle);
    const initialBoundaryVertices = new Set<number>(this.mesh.getBorderVertices().map((v) => v.id));
    const originalVerticesIds = new Set<number>(this.mesh.halfEdges.vertices.map((v) => v.id));

    type QueueEntry = { vertex: Vertex; depth: number; seed: number; active: boolean };
    const queue: QueueEntry[] = [];
    const scheduled = new Map<Vertex, QueueEntry>();
    const processed = new Set<number>();
    let seedCounter = 0;

    const enqueue = (vertex: Vertex | null, depth: number) => {
      if (!vertex) return;
      if (!vertex.isFree()) return;
      if (processed.has(vertex.id)) return;
      const existing = scheduled.get(vertex);
      if (existing && existing.active) return;
      const entry: QueueEntry = { vertex, depth, seed: seedCounter++, active: true };
      scheduled.set(vertex, entry);
      queue.push(entry);
    };

    const popNext = (): QueueEntry | undefined => {
      while (queue.length > 0) {
        let bestIndex = -1;
        let bestEntry: QueueEntry | undefined;
        for (let i = 0; i < queue.length; i++) {
          const entry = queue[i];
          if (!entry.active) continue;
          if (processed.has(entry.vertex.id)) {
            entry.active = false;
            scheduled.delete(entry.vertex);
            continue;
          }
          if (!entry.vertex.isFree()) {
            entry.active = false;
            scheduled.delete(entry.vertex);
            continue;
          }
          if (!bestEntry ||
              entry.depth < bestEntry.depth ||
              (entry.depth === bestEntry.depth && heightOf(entry.vertex) > heightOf(bestEntry.vertex)) ||
              (entry.depth === bestEntry.depth && heightOf(entry.vertex) === heightOf(bestEntry.vertex) && entry.seed < bestEntry.seed)) {
            bestEntry = entry;
            bestIndex = i;
          }
        }
        if (bestIndex === -1) {
          queue.length = 0;
          return undefined;
        }
        const entry = queue.splice(bestIndex, 1)[0];
        entry.active = false;
        scheduled.delete(entry.vertex);
        if (processed.has(entry.vertex.id)) {
          continue;
        }
        return entry;
      }
      return undefined;
    };

    const pickSeedVertex = () => {
      const border = this.mesh.getBorderVertices();
      if (border.length === 0) {
        return null;
      }
      let best = border[0];
      let bestScore = heightOf(best);
      for (let i = 1; i < border.length; i++) {
        const candidate = border[i];
        const score = heightOf(candidate);
        if (score > bestScore) {
          best = candidate;
          bestScore = score;
        }
      }
      return best;
    };

    const seedVertex = pickSeedVertex();
    if (seedVertex) {
      enqueue(seedVertex, 0);
    }

    while (this.mesh.getBorderVertices().length !== 0 && facesAdded < maxFaces) {
      if (currentIteration++ >= this.maxIterations) break;

      let current = popNext();
      if (!current) {
        const newSeed = pickSeedVertex();
        if (newSeed) {
          enqueue(newSeed, 0);
          current = popNext();
        }
        if (!current) break;
      }

      const center = current.vertex;
      if (processed.has(center.id)) {
        continue;
      }
      if (!center.isFree()) {
        processed.add(center.id);
        continue;
      }

      const boundaryInfo = center.getBoundaryHalfedgesAndVerticesInfo();
      if (!boundaryInfo || !this.areVerticesOnBoundary(boundaryInfo)) {
        processed.add(center.id);
        vertexToBoundaryInfo.delete(center);
        continue;
      }

      const existingInfo = vertexToBoundaryInfo.get(center);
      if (existingInfo) {
        front.remove(existingInfo);
      }
      vertexToBoundaryInfo.set(center, boundaryInfo);
      front.insert(boundaryInfo);

      const preFaceCount = this.mesh.halfEdges.faces.length;
      const newIterationVertices: Vertex[] = [];

      if (boundaryInfo.angle < 75) {
        await this.handleSmallAngle(boundaryInfo, vertexToBoundaryInfo, front);
      } else if (boundaryInfo.angle >= 75 && boundaryInfo.angle < 125) {
        const newVertex = await this.handleMediumAngle(boundaryInfo, vertexToBoundaryInfo, front);
        if (newVertex) newIterationVertices.push(newVertex);
      } else {
        const newVertices = await this.handleLargeAngle(boundaryInfo, vertexToBoundaryInfo, front);
        newIterationVertices.push(...newVertices);
      }

      if (newIterationVertices.length > 0) {
        await this.processVertexMerging(newIterationVertices, vertexToBoundaryInfo, front, initialBoundaryVertices);
      }

      this.mesh.calculateBorderVertices();

      const postFaceCount = this.mesh.halfEdges.faces.length;
      const facesCreated = Math.max(0, postFaceCount - preFaceCount);
      facesAdded += facesCreated;

      front.remove(boundaryInfo);
      vertexToBoundaryInfo.delete(center);
      processed.add(center.id);

      if (facesCreated === 0) {
        stalledIterations++;
        if (stalledIterations > this.mesh.getBorderVertices().length * 2) {
          break;
        }
        continue;
      }

      stalledIterations = 0;

      if (facesAdded >= maxFaces) break;

      enqueue(boundaryInfo.previousVertex, current.depth + 1);
      enqueue(boundaryInfo.nextVertex, current.depth + 1);
      enqueue(boundaryInfo.outgoingHalfedge.vertex, current.depth + 1);
      for (const vertex of newIterationVertices) {
        enqueue(vertex, current.depth + 1);
      }
    }

    const patchedIds: Set<number> = new Set();
    this.mesh.halfEdges.vertices.forEach((v) => {
      if (!originalVerticesIds.has(v.id)) patchedIds.add(v.id);
    });
    this.mesh.calculateBorderVertices();
    return { patchedIds, facesAdded };
  }

  private async handleSmallAngle(
    boundaryInfo: adjacenceBoundaries,
    vertexToBoundaryInfo: Map<Vertex, adjacenceBoundaries>,
    front: SortedArray<adjacenceBoundaries>,
  ): Promise<void> {
    if (boundaryInfo.previousVertex === boundaryInfo.nextVertex) {
      return;
    }

    const h3 = this.mesh.halfEdges.addEdge(boundaryInfo.previousVertex, boundaryInfo.nextVertex);

    this.mesh.halfEdges.addFace([boundaryInfo.incomingHalfedge, boundaryInfo.outgoingHalfedge, h3.twin]);

    this.updateBoundaryVertices(boundaryInfo, vertexToBoundaryInfo, front);
  }

  private async handleMediumAngle(
    boundaryInfo: adjacenceBoundaries,
    vertexToBoundaryInfo: Map<Vertex, adjacenceBoundaries>,
    front: SortedArray<adjacenceBoundaries>,
  ): Promise<Vertex | null> {
    if (!boundaryInfo.outgoingHalfedge.vertex.isFree()) {
      return null;
    }

    const newVertex = this.createOptimalVertex(boundaryInfo.previousVertex, boundaryInfo.outgoingHalfedge.vertex, boundaryInfo.nextVertex);

    if (!this.areVerticesOnBoundary(boundaryInfo)) {
      return null;
    }

    const h3 = this.mesh.halfEdges.addEdge(boundaryInfo.previousVertex, newVertex);
    const h4 = this.mesh.halfEdges.addEdge(boundaryInfo.outgoingHalfedge.vertex, newVertex);
    const h5 = this.mesh.halfEdges.addEdge(newVertex, boundaryInfo.nextVertex);

    this.mesh.halfEdges.addFace([boundaryInfo.incomingHalfedge, h4, h3.twin]);
    this.mesh.halfEdges.addFace([h4.twin, boundaryInfo.outgoingHalfedge, h5.twin]);

    this.updateBoundaryVertices(boundaryInfo, vertexToBoundaryInfo, front, newVertex);

    return newVertex;
  }

  private async handleLargeAngle(
    boundaryInfo: adjacenceBoundaries,
    vertexToBoundaryInfo: Map<Vertex, adjacenceBoundaries>,
    front: SortedArray<adjacenceBoundaries>,
  ): Promise<Vertex[]> {
    if (!boundaryInfo.outgoingHalfedge.vertex.isFree()) {
      return [];
    }

    const newVertices = this.createTwoOptimalVertices(boundaryInfo.previousVertex, boundaryInfo.outgoingHalfedge.vertex, boundaryInfo.nextVertex);

    if (!this.areVerticesOnBoundary(boundaryInfo)) {
      return [];
    }

    const [newVertex1, newVertex2] = newVertices;

    const h3 = this.mesh.halfEdges.addEdge(boundaryInfo.previousVertex, newVertex1);
    const h4 = this.mesh.halfEdges.addEdge(boundaryInfo.outgoingHalfedge.vertex, newVertex1);
    const h5 = this.mesh.halfEdges.addEdge(boundaryInfo.outgoingHalfedge.vertex, newVertex2);
    const h6 = this.mesh.halfEdges.addEdge(newVertex2, boundaryInfo.nextVertex);
    const h7 = this.mesh.halfEdges.addEdge(newVertex1, newVertex2);

    this.mesh.halfEdges.addFace([boundaryInfo.incomingHalfedge, h4, h3.twin]);
    this.mesh.halfEdges.addFace([h4.twin, h5, h7.twin]);
    this.mesh.halfEdges.addFace([h5.twin, boundaryInfo.outgoingHalfedge, h6.twin]);

    this.updateBoundaryVertices(boundaryInfo, vertexToBoundaryInfo, front, newVertex1, newVertex2);

    return newVertices;
  }

  private areVerticesOnBoundary(boundaryInfo: adjacenceBoundaries): boolean {
    return boundaryInfo.previousVertex.isFree() && boundaryInfo.nextVertex.isFree() && boundaryInfo.outgoingHalfedge.vertex.isFree();
  }

  private updateBoundaryVertices(
    boundaryInfo: adjacenceBoundaries,
    vertexToBoundaryInfo: Map<Vertex, adjacenceBoundaries>,
    front: SortedArray<adjacenceBoundaries>,
    ...newVertices: Vertex[]
  ): void {
    this.updateBoundaryInfo(boundaryInfo.previousVertex, vertexToBoundaryInfo, front);
    this.updateBoundaryInfo(boundaryInfo.nextVertex, vertexToBoundaryInfo, front);
    this.updateBoundaryInfo(boundaryInfo.outgoingHalfedge.vertex, vertexToBoundaryInfo, front);

    for (const vertex of newVertices) {
      this.updateBoundaryInfo(vertex, vertexToBoundaryInfo, front);
    }
  }

  private updateBoundaryInfo(vertex: Vertex, vertexToBoundaryInfo: Map<Vertex, adjacenceBoundaries>, front: SortedArray<adjacenceBoundaries>): void {
    const oldInfo = vertexToBoundaryInfo.get(vertex);
    const newInfo = vertex.getBoundaryHalfedgesAndVerticesInfo();
    if (oldInfo) {
      if (newInfo) {
        // Vertex is still on boundary, update its info
        front.update(oldInfo, newInfo);
        vertexToBoundaryInfo.set(vertex, newInfo);
      } else {
        // Vertex is no longer on boundary, remove it from front
        front.remove(oldInfo);
        vertexToBoundaryInfo.delete(vertex);
      }
    } else if (newInfo) {
      front.insert(newInfo);
    }
  }

  private async processVertexMerging(
    newlyCreatedVertices: Vertex[],
    vertexToBoundaryInfo: Map<Vertex, adjacenceBoundaries>,
    front: SortedArray<adjacenceBoundaries>,
    initialBoundaryVertices: Set<number>,
  ): Promise<void> {
    if (this.applyVertexMerges(newlyCreatedVertices, this.mergeThreshold, vertexToBoundaryInfo, front, initialBoundaryVertices).length > 0) {
      this.mesh.setBorderVertices(this.mesh.halfEdges.vertices.filter((v) => v.isFree()));
    }
  }

  /**
   * Applies iterative vertex merging by finding closest pairs and merging them one by one.
   * Each merge creates new opportunities for merging as vertices move closer together.
   *
   * @param newVertices Array of newly created vertices to consider for merging
   * @param threshold The distance threshold for merging vertices
   * @param vertexToBoundaryInfo Map of vertex to boundary info
   * @param front Sorted array of boundary vertices
   * @returns Number of successful merges performed
   */
  public applyVertexMerges(
    newVertices: Vertex[],
    threshold: number,
    vertexToBoundaryInfo: Map<Vertex, adjacenceBoundaries>,
    front: SortedArray<adjacenceBoundaries>,
    initialBoundaryVertices: Set<number>,
  ): Array<Vertex> {
    let successfulMerges: Array<Vertex> = [];

    while (newVertices.length > 0) {
      const actualVertex = newVertices.pop()!;

      // Skip if vertex is no longer on boundary
      if (!actualVertex.isFree()) {
        continue;
      }

      // Skip if this is an initial boundary vertex - don't merge original boundary vertices
      if (initialBoundaryVertices.has(actualVertex.id)) {
        continue;
      }

      const boundaryNeighbors: Vertex[] = actualVertex.getAdjacenceBoundaryVertices();

      let closestPair: { source: Vertex; target: Vertex; distance: number } | null = null;
      let minDistance = threshold;

      // Find the closest pair of vertices within threshold
      for (const sourceVertex of boundaryNeighbors) {
        // Skip if source vertex is no longer on boundary
        if (!sourceVertex.isFree()) {
          continue;
        }

        // Skip initial boundary vertices - don't merge with original boundary
        if (initialBoundaryVertices.has(sourceVertex.id)) {
          continue;
        }

        const distance = sourceVertex.position.distanceTo(actualVertex.position);
        if (distance < minDistance) {
          minDistance = distance;
          closestPair = { source: sourceVertex, target: actualVertex, distance };
        }
      }

      if (!closestPair || !closestPair.source.isFree() || !closestPair.target.isFree()) {
        continue;
      }

      // Remove old boundary info before merging
      const oldInfo = vertexToBoundaryInfo.get(closestPair.source);
      if (oldInfo) {
        front.remove(oldInfo);
        vertexToBoundaryInfo.delete(closestPair.source);
      }

      // Perform the merge
      this.mesh.halfEdges.edgeCollapse(closestPair.source, closestPair.target);

      boundaryNeighbors.forEach((v) => {
        if (!v.halfedge) {
          const info = vertexToBoundaryInfo.get(v);
          if (info) {
            front.remove(info);
            vertexToBoundaryInfo.delete(v);
          }
        }
      });

      // Update boundary info for the target vertex
      this.updateBoundaryInfo(closestPair.target, vertexToBoundaryInfo, front);
      newVertices.push(closestPair.target);

      successfulMerges.push(closestPair.target);
    }

    return successfulMerges;
  }

  /**
   * Creates a new vertex positioned optimally for advancing front triangulation.
   * The vertex is placed in the same plane as the 3 boundary vertices and positioned
   * on the angle bisector of the angle at v2, dividing the angle by 2 when creating triangles.
   * This version correctly handles concave angles to ensure the new vertex is created inside the hole.
   *
   * @param v1 First boundary vertex (previous vertex)
   * @param v2 Second boundary vertex (the "current" vertex with the angle to be bisected)
   * @param v3 Third boundary vertex (next vertex)
   * @returns New vertex positioned for optimal triangulation on the angle bisector
   */
  private createOptimalVertex(v1: Vertex, v2: Vertex, v3: Vertex): Vertex {
    // Calculate vectors from v2 to v1 and v2 to v3
    const vec1 = new Vector3().subVectors(v1.position, v2.position);
    const vec3 = new Vector3().subVectors(v3.position, v2.position);

    // Calculate the angle bisector - this gives us the direction to place the new vertex
    const bisector = new Vector3().addVectors(vec1.normalize(), vec3.normalize()).normalize();

    // For boundary holes, we need to determine if we should place the vertex on the
    // positive or negative side of the bisector. We can use the cross product of the
    // boundary edge vectors to determine the orientation.
    const cross = new Vector3().crossVectors(vec1, vec3);

    // Get a reference face normal from the mesh to determine which side is "inside"
    let meshNormal = new Vector3();
    const boundaryInfo = v2.getBoundaryHalfedgesAndVerticesInfo();
    if (boundaryInfo) {
      if (boundaryInfo.incomingHalfedge.twin.face) {
        boundaryInfo.incomingHalfedge.twin.face.getNormal(meshNormal);
      } else if (boundaryInfo.outgoingHalfedge.twin.face) {
        boundaryInfo.outgoingHalfedge.twin.face.getNormal(meshNormal);
      }
    }

    // The key insight: for a hole in a mesh, we want to create vertices "inward"
    // toward the inside of the original mesh surface. If the cross product and
    // mesh normal point in the same direction, we need to flip the bisector to
    // point inward (into the hole).
    if (cross.dot(meshNormal) > 0) {
      bisector.negate();
    }

    // Calculate distance - use a fraction of the minimum edge length for good triangle quality
    const dist1 = v1.position.distanceTo(v2.position);
    const dist2 = v2.position.distanceTo(v3.position);
    const optimalDistance = Math.min(dist1, dist2) * 0.5; // Use half the edge length

    // Position the new vertex along the corrected bisector
    const newPosition = v2.position.clone().addScaledVector(bisector, optimalDistance);

    // Create and return the new vertex
    return this.mesh.halfEdges.addVertex(newPosition, undefined, new Vector3(1, 1, 1));
  }

  /**
   * Creates two new vertices positioned optimally for advancing front triangulation
   * when dealing with very large angles (≥125°). The vertices are positioned to
   * divide the large angle into three roughly equal parts, creating better triangle quality.
   *
   * @param v1 First boundary vertex (previous vertex)
   * @param v2 Second boundary vertex (the "current" vertex with the large angle to be divided)
   * @param v3 Third boundary vertex (next vertex)
   * @returns Array of two new vertices positioned for optimal triangulation
   */
  private createTwoOptimalVertices(v1: Vertex, v2: Vertex, v3: Vertex): [Vertex, Vertex] {
    // Calculate vectors from v2 to v1 and v2 to v3
    const vec1 = new Vector3().subVectors(v1.position, v2.position).normalize();
    const vec3 = new Vector3().subVectors(v3.position, v2.position).normalize();

    // Calculate the angle between the two vectors
    const angle = vec1.angleTo(vec3);

    // We want to divide this angle into 3 equal parts
    const angleThird = angle / 3;

    // Create rotation axis (perpendicular to both vectors)
    const rotationAxis = new Vector3().crossVectors(vec1, vec3).normalize();

    // Check for inward direction using mesh normal
    let meshNormal = new Vector3();
    const boundaryInfo = v2.getBoundaryHalfedgesAndVerticesInfo();
    if (boundaryInfo) {
      if (boundaryInfo.incomingHalfedge.twin.face) {
        boundaryInfo.incomingHalfedge.twin.face.getNormal(meshNormal);
      } else if (boundaryInfo.outgoingHalfedge.twin.face) {
        boundaryInfo.outgoingHalfedge.twin.face.getNormal(meshNormal);
      }
    }

    // Determine rotation direction - if cross product points same way as mesh normal,
    // we need to flip the rotation axis to rotate inward
    if (rotationAxis.dot(meshNormal) > 0) {
      rotationAxis.negate();
    }

    // Create two direction vectors by rotating vec1 by 1/3 and 2/3 of the angle
    const direction1 = vec1.clone();
    direction1.applyAxisAngle(rotationAxis, angleThird);

    const direction2 = vec1.clone();
    direction2.applyAxisAngle(rotationAxis, 2 * angleThird);

    // Calculate optimal distance based on edge lengths
    const dist1 = v1.position.distanceTo(v2.position);
    const dist2 = v2.position.distanceTo(v3.position);
    const optimalDistance = Math.min(dist1, dist2) * 0.5;

    // Position the new vertices along the calculated directions
    const newPosition1 = v2.position.clone().addScaledVector(direction1, optimalDistance);
    const newPosition2 = v2.position.clone().addScaledVector(direction2, optimalDistance);

    // Create vertices
    const newVertex1 = this.mesh.halfEdges.addVertex(newPosition1, undefined, new Vector3(1, 1, 1));
    const newVertex2 = this.mesh.halfEdges.addVertex(newPosition2, undefined, new Vector3(1, 1, 1));

    return [newVertex1, newVertex2];
  }
}
